import { MaFundsSettingsExportExcelData }
  from 'src/app/domain/recovery/component/recovery-market-analysis/ma-funds-settings/ma-funds-settings.component';
import { Rows } from '../../../interfaces';
import { DataType, formats, getCell, spaceInsteadOfNull, withCellStyles } from '../../../utils';
import { MaFund } from 'src/app/domain/recovery/component/recovery-market-analysis/pojo/ma-classes';
import { boldLightGrayBgLeft, left } from 'src/app/util/excelStyles';


export const getPreparedData = (data: MaFundsSettingsExportExcelData): Rows => {
  const newData: Rows = [];
  newData.push(data.headers);
  const includedFunds = data.funds.filter((f) => f.isSelected);
  dataLengthForTotalCount = includedFunds.length + 2;
  const totals = getTotals(data.total, data.headers);
  newData.push(...totals);
  const mainRows = getManyRows(includedFunds, data.headers);
  newData.push(...mainRows);

  const notIncludedFunds = data.funds.filter((fund) => !fund.isSelected);
  if (notIncludedFunds.length) {
    newData.push(['Not included']);
    currentRowIndex++;
    const notIncludedRows = getManyRows(notIncludedFunds, data.headers);
    newData.push(...notIncludedRows);
  }
  resetData();
  return newData;
};

const getTotals = (totals: MaFund, headers: string[]): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewTotalMapping(totals);
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header] as DataType, boldLightGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;
};

const getManyRows = (funds: MaFund[], headers: string[]): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = funds.map((fund: MaFund) => getNewMapping(fund));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header] as DataType, left));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMapping = (fund: MaFund): object => {
  const body = {
    '': fund.isSelected ? 'Yes' : 'No',
    'Fund name': fund.name,
    'Registration date':
      fund.registrationDate ?? '',
    'Initial closing': fund.initialClosing ?? '',
    'Vintage year': fund.vintageYear ?? '',
    'Total commitment':
      getCell({
        v: spaceInsteadOfNull(fund.totalCommitment),
        z: formats.thousandsSeparator,
        t: 'n',
      }),
    'Contribution received %': getCell({
      f: `IFERROR(H${currentRowIndex}/F${currentRowIndex},0)`,
      z: formats.percents,
      t: 'n',
    }),
    'Contribution received $': getCell({
      v: spaceInsteadOfNull(fund.contributionReceived),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Total expenses': getCell({
      v: spaceInsteadOfNull(fund.totalExpenses),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Net gain/(loss)': getCell({
      f: `S${currentRowIndex}-I${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'NAV & Distribution': getCell({
      f: `SUM(H${currentRowIndex},J${currentRowIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    TVPI: getCell({
      f: `IFERROR(K${currentRowIndex}/H${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    Distribution: getCell({
      v: spaceInsteadOfNull(fund.distribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    DPI: getCell({
      f: `IFERROR(M${currentRowIndex}/H${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Net IRR': getCell({
      v: spaceInsteadOfNull(fund.netIrr),
      z: formats.percents,
      t: 'n',
    }),
    'Total companies': getCell({
      v: spaceInsteadOfNull(fund.amountOfCompanies),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Investment: getCell({
      v: spaceInsteadOfNull(fund.investment),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Value: getCell({
      v: spaceInsteadOfNull(fund.value),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)': getCell({
      f: `R${currentRowIndex}-Q${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    MOIC: getCell({
      f: `IFERROR(R${currentRowIndex}/Q${currentRowIndex}, 0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Portfolio IRR': getCell({
      v: spaceInsteadOfNull(fund.grossIrr),
      z: formats.percents,
      t: 'n',
    }),
    'Sold: Companies': getCell({
      v: spaceInsteadOfNull(fund.amountOfSoldCompanies),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Sold: Investment': getCell({
      v: spaceInsteadOfNull(fund.soldInvestment),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Sold: Value': getCell({
      v: spaceInsteadOfNull(fund.soldValue),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Sold: realized gain/(loss)': getCell({
      f: `X${currentRowIndex}-W${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Sold: MOIC': getCell({
      f: `IFERROR(X${currentRowIndex}/W${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Sold: IRR': getCell({
      v: spaceInsteadOfNull(fund.soldIrr),
      z: formats.percents,
      t: 'n',
    }),
    'Sold: investment/total commitment': getCell({
      f: `IFERROR(W${currentRowIndex}/F${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
  };
  currentRowIndex++;
  return body;
};

const getNewTotalMapping = (total: MaFund): [object] => {
  const body = {
    '': 'Included in statistics',
    'Fund name': 'Total',
    'Registration date': '',
    'Initial closing': '',
    'Vintage year': '',
    'Total commitment':
      getCell({
        f: `SUM(F${currentRowIndex + 1}:F${dataLengthForTotalCount})`,
        z: formats.thousandsSeparator,
        t: 'n',
      }),
    'Contribution received %': getCell({
      f: `IFERROR(H${currentRowIndex}/F${currentRowIndex},0)`,
      z: formats.percents,
      t: 'n',
    }),
    'Contribution received $': getCell({
      f: `SUM(H${currentRowIndex + 1}:H${dataLengthForTotalCount})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Total expenses': getCell({
      f: `SUM(I${currentRowIndex + 1}:I${dataLengthForTotalCount})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Net gain/(loss)': getCell({
      f: `S${currentRowIndex}-I${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'NAV & Distribution': getCell({
      f: `SUM(H${currentRowIndex},J${currentRowIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    TVPI: getCell({
      f: `IFERROR(K${currentRowIndex}/H${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    Distribution: getCell({
      f: `SUM(M${currentRowIndex + 1}:M${dataLengthForTotalCount})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    DPI: getCell({
      f: `IFERROR(M${currentRowIndex}/H${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Net IRR': getCell({
      v: spaceInsteadOfNull(total.netIrr),
      z: formats.percents,
      t: 'n',
    }),
    'Total companies': getCell({
      f: `SUM(P${currentRowIndex + 1}:P${dataLengthForTotalCount})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Investment: getCell({
      f: `SUM(Q${currentRowIndex + 1}:Q${dataLengthForTotalCount})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Value: getCell({
      f: `SUM(R${currentRowIndex + 1}:R${dataLengthForTotalCount})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)': getCell({
      f: `R${currentRowIndex}-Q${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    MOIC: getCell({
      f: `IFERROR(R${currentRowIndex}/Q${currentRowIndex}, 0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Portfolio IRR': getCell({
      v: spaceInsteadOfNull(total.grossIrr),
      z: formats.percents,
      t: 'n',
    }),
    'Sold: Companies': getCell({
      f: `SUM(V${currentRowIndex + 1}:V${dataLengthForTotalCount})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Sold: Investment': getCell({
      f: `SUM(W${currentRowIndex + 1}:W${dataLengthForTotalCount})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Sold: Value': getCell({
      f: `SUM(X${currentRowIndex + 1}:X${dataLengthForTotalCount})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Sold: realized gain/(loss)': getCell({
      f: `X${currentRowIndex}-W${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Sold: MOIC': getCell({
      f: `IFERROR(X${currentRowIndex}/W${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Sold: IRR': getCell({
      v: spaceInsteadOfNull(total.soldIrr),
      z: formats.percents,
      t: 'n',
    }),
    'Sold: investment/total commitment': getCell({
      v: total.soldInvestment / total.totalCommitment,
      f: `IFERROR(W${currentRowIndex}/F${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
  };
  currentRowIndex++;
  return [body];
};


const resetData = (): void => {
  currentRowIndex = 2;
  dataLengthForTotalCount = 0;
};

let currentRowIndex = 2;
let dataLengthForTotalCount = 0;

